// @ts-nocheck

/*
  Clicking on a tab hides all panels
  and only shows the one corresponding to the tab.
  The active tab gets an 'active' css class.
  Tabs are switched by changing the location hash,
  which means, you can link to a tab by adding an hash.

  Expects a list of tabs and corresponding panels:

  <div data-behavior="tabs">
    <a href="#panel1" class="active">1</a>
    <a href="#panel2">2</a>
  </div>

  <div data-panel-id="panel1" class="js-cobot-panel"></div>
  <div data-panel-id="panel2" class="js-cobot-panel hidden"></div>

  Tabs must link to [data-panel-id] of the corresponding panel (anchor link).
  First tab needs initial 'active' class, all panels but first need class "hidden" initially.
  All panels must have the class js-cobot-panel.

  If one of the panels has form elements with the 'input--error' class in it, it is shown immediately
  and the no. of errors is added to the tab.

  If the tabs are surrounded by a form and the form is submitted with invalid fields,
  the first tab with invalid fields is shown.
*/
import getParents from './get-parents';

export default function tabs() {
  window.addEventListener('hashchange', tabChanged, false);
  const tabLists = [];

  // find the tab that matches the contents of the browser location hash and
  // switch to it
  function tabChanged() {
    for (let list of tabLists) {
      list.switchToTab(location.hash.replace('#', ''));
    }
  }

  // find and initialize all tab lists on the page
  document.querySelectorAll('[data-behavior=tabs]').forEach((list) => {
    const tabList = TabList(list);
    tabLists.push(tabList);
    if (location.hash) {
      tabChanged();
    }
  });

  return tabChanged;
}
/**
 * Initializes the tabs.
 *
 * @param  {Element} list the container containing the tab links
 */
function TabList(list) {
  const panels = findPanels();

  // html5 errors
  panels.forEach((panel) => attachShowOnInvalid(panel));

  // server side errors
  highlightServerErrors();

  return {
    /**
     * Show the tab with the given id, hide all others.
     * @param  {String} panelId the [data-panel-id] of the panel to show
     */
    switchToTab(panelId) {
      const panel = document.querySelector(`[data-panel-id=${panelId}]`);
      const link = list.querySelector(`a[href='#${panelId}']`);
      if (panel) {
        panels.forEach((panel) => panel.classList.add('hidden'));
        panel.classList.remove('hidden');
        list
          .querySelectorAll('a')
          .forEach((link) => link.classList.remove('active'));
        link.classList.add('active');
      }
    },
  };

  function findPanels() {
    const panelIds = [];
    list.querySelectorAll('a').forEach((li) => {
      if (li.getAttribute('href')[0] === '#') {
        panelIds.push(li.getAttribute('href').replace('#', ''));
      }
    });
    return document.querySelectorAll(
      panelIds.map((id) => `[data-panel-id=${id}]`).join(', '),
    );
  }

  /**
   * Attaches listeners to all inputs in the panel that wait for an "invalid"
   * event and show the panel when the event occurs.
   *
   * @param  {Element} panel
   */
  function attachShowOnInvalid(panel) {
    panel.querySelectorAll('input,select').forEach((input) => {
      // invalid event does not bubble so we have to attach to every input
      input.addEventListener('invalid', (/** @type {Event} */ event) => {
        const invalidInput = event.target;
        const surroundingPanel = getParents(invalidInput, '.js-cobot-panel')[0];
        if (surroundingPanel) {
          switchToTab(surroundingPanel.dataset.panelId);
          input.scrollIntoView();
        }
      });
    });
  }

  /**
   * Immediately shows the first panel that has form errors
   * produced on the server and adds an error counter to the tab.
   */
  function highlightServerErrors() {
    const errorPanels = getParents(
      document.querySelector('.js-cobot-panel .input--error'),
      '.js-cobot-panel',
    );
    if (errorPanels.length) {
      errorPanels.forEach((panel) => {
        const tab = list.querySelector(`a[href='#${panel.dataset.panelId}']`);
        location.hash = tab.getAttribute('href');
        tab.innerHTML = `${tab.innerHTML} <span class="tab-error">${
          panel.querySelectorAll('.input--error').length
        }</span>`;
      });
    }
  }
}

/**
 * Switch to the tab with the given id by changing the browser location.
 *
 * @param {String} panelId the [data-panel-id] of the panel to show
 */
function switchToTab(panelId) {
  location.hash = panelId;
}
